// Inspector gestures (DES2): the small decisions the host layer makes about a person's input.
// Pure, so every rule is tested without a browser. These decide only pointer and keyboard
// intent; selection, scope and authority stay with INS.

/** Movement beyond this is a drag (scroll), not a tap: scrolling must never select. */
export const DRAG_PX = 8;

export function classifyPointer(
  down: { x: number; y: number },
  up: { x: number; y: number },
): 'tap' | 'drag' {
  return Math.hypot(up.x - down.x, up.y - down.y) > DRAG_PX ? 'drag' : 'tap';
}

/** A plain click replaces the set; ⌘/Ctrl-click or the "Add another" toggle adds or removes. */
export function selectionMode(
  event: { metaKey?: boolean; ctrlKey?: boolean },
  addMode: boolean,
): 'add' | 'replace' {
  return event.metaKey || event.ctrlKey || addMode ? 'add' : 'replace';
}

/** Escape, in order: cancel a pending edit, then clear the selection, then leave Inspect. */
export function escapeStep(state: {
  editing: boolean;
  selected: number;
  on: boolean;
}): 'cancel-edit' | 'clear' | 'leave' | null {
  if (state.editing) return 'cancel-edit';
  if (state.selected > 0) return 'clear';
  if (state.on) return 'leave';
  return null;
}

type Typing = {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
};

/** No inspector shortcut fires while someone types, or inside a terminal. */
export function isTypingTarget(target: Typing | null | undefined): boolean {
  if (!target) return false;
  const tag = (target.tagName ?? '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest?.('[contenteditable="true"], [data-terminal]'));
}

/**
 * Hover hit tests: at most 30 a second and one outstanding (INS C16 bounds). A pointer move
 * that can't be sent now is remembered, and the latest one goes when the outstanding answer
 * arrives; older moves are dropped, never queued.
 */
export class HoverGate {
  static readonly MIN_INTERVAL_MS = 1000 / 30;
  private lastSentAt = -Infinity;
  private outstanding = false;
  private waiting: { x: number; y: number } | null = null;

  /** Offer a point; returns it when it may be sent now, otherwise keeps it as the latest. */
  offer(point: { x: number; y: number }, now: number): { x: number; y: number } | null {
    if (this.outstanding || now - this.lastSentAt < HoverGate.MIN_INTERVAL_MS) {
      this.waiting = point;
      return null;
    }
    this.lastSentAt = now;
    this.outstanding = true;
    this.waiting = null;
    return point;
  }

  /** The answer arrived (or was discarded); returns a waiting point to send next, if any. */
  settle(now: number): { x: number; y: number } | null {
    this.outstanding = false;
    const next = this.waiting;
    if (!next) return null;
    return this.offer(next, now);
  }

  /** Milliseconds until a waiting point may be sent (0 when it may go now, Infinity while one is out). */
  msUntilReady(now: number): number {
    if (this.outstanding) return Infinity;
    return Math.max(0, this.lastSentAt + HoverGate.MIN_INTERVAL_MS - now);
  }

  /** Send the waiting point if the interval has passed and nothing is outstanding. */
  flush(now: number): { x: number; y: number } | null {
    const next = this.waiting;
    if (!next || this.outstanding) return null;
    return this.offer(next, now);
  }

  /** A point is waiting for the interval or the outstanding answer. */
  get waitingPoint(): { x: number; y: number } | null {
    return this.waiting;
  }

  reset(): void {
    this.outstanding = false;
    this.waiting = null;
    this.lastSentAt = -Infinity;
  }
}
